/**
 * @file   open-app.js 打开app
 * @author zhengchanglong<zhengchanglong@baidu.com>
 * @date   2020-11-03
 * @doc    http://agroup.baidu.com/share/md/dee79bc8077a4318b30e978bdf8bdeba
 * @doc    http://cp02-wangjuntao.bcc-bdbl.baidu.com:8001/matrix-guide/part2/launch3rdApp.html
 * @doc    http://wiki.baidu.com/pages/viewpage.action?pageId=376255077
 */
import {pickup, isPlain, copy2clipboard, guid} from '@baidu/exp-utils';
import {invokeApp} from '@baidu/xbox-vertical-na/openBox';
import {IS_BAIDU, IS_WECHAT, IS_QQ} from '@utils/detect';
import {getDownloadLinks, showGuide, registerDownloadChannel} from '@utils/download-app';
import {isIOS, getOSVersion} from '@baidu/xbox/os';
import versionCompare from '@baidu/xbox/version_compare';
// import {PAGE} from '@config/index';
import {launch3rdApp} from '@baidu/eop-matrix/es/native/appLoader';
// import {isBaiduHost} from '@utils/url';
import Toast from '@utils/customToast';

const SCHEME_PREFIX_OF_OPEN_APP_NA_PAGE = 'fortunecat://invoke?p=';
const SCHEME_PREFIX_OF_OPEN_APP_H5_PAGE = 'fortunecat://invokeh5?p=';
const SCHEME_PREFIX_OF_OPEN_APP_SWAN_PAGE = 'fortunecat://swan/s1RSsNQbWmxAuXu05icm1lw0GCyH25NM/';
const SCHEME_PREFIX_OF_OPEN_APP_LIVE_BANNER = 'fortunecat://livebanner?p=';
const SCHEME_PREFIX_OF_OPEN_APP_LIVE_ROOM = 'fortunecat://liveroom?p=';
const SCHEME_PREFIX_OF_OPEN_APP_LIVE_PLAYBACK = 'fortunecat://liveplayback?p=';
const SCHEME_PREFIX_OF_OPEN_BDBOX_SWAN_PAGE = 'baiduboxapp://swan/s1RSsNQbWmxAuXu05icm1lw0GCyH25NM/';
const ENCODE_APP_STORE_LINK = encodeURIComponent(getDownloadLinks().appStore);
const UNIVERSAL_LINK_PREFIX = `https://wakeup.baidu.com/fortunecat/scheme?target=${ENCODE_APP_STORE_LINK}&p=`;

// 由于多数情况下手百/微信/QQ会禁掉h5的schema跳转，所以这里默认的延时时0，确保更快的下载
const WAIT_TIME = (IS_BAIDU || IS_WECHAT || IS_QQ) ? 0 : 2000;
const CAN_I_USE_ULINK = (isIOS() && versionCompare(getOSVersion(), '9.0') >= 0);
// 控制是否使用端能力调起/下载
// const CAN_I_USE_BDBOX_ABILITY = IS_BAIDU && isBaiduHost() && !isIOS();

/**
 * 打开app默认失败回调
 *
 * @param {string} scheme scheme字符串
 * @param {string} newCN 新增渠道号
 * @param {string} invokeCN 唤起渠道号
 */
const openFailCbk = function (scheme, newCN, invokeCN) {
    // 2021.10 古物潮玩APP下线 引流下载功能取消
    // let url = '';
    // const links = getDownloadLinks(newCN);
    // url = isIOS() ? links.appStore : links.apk;

    if (IS_WECHAT || IS_QQ) {
        showGuide();
        return;
    }
    // if (CAN_I_USE_BDBOX_ABILITY) {
    //     downloadInBdbox(url);
    //     return;
    // }

    // location.href = url || PAGE.home;
    const tips = '请到百度APP搜索『古物潮玩』小程序查看更多';
    Toast.init();
    Toast.show(tips);
};

/**
 * 打开端原生页面
 * 如果对params的参数配置不了解，可以参考文件头部规范文档
 *
 * @param {Object|string} params scheme或者是生成scheme必要的参数
 * @param {string} params.id 页面id
 * @param {number} params.type 卡片类型
 * @param {string} params.version 卡片版本
 * @param {string} params.source 来源
 * @param {Object} params.ext 扩展参数
 * @param {Object} opts 自定义配置
 * @param {Function} opts.fail 失败回调
 * @param {number} opts.wait 失败回调的定时，毫秒
 * @param {boolean} opts.useCopy 是否复制剪切板
 * @param {boolean} opts.newCN 新增渠道号
 * @param {boolean} opts.invokeCN 唤起渠道号
 * @param {boolean} opts.useULink 默认使用ulink，如果想禁用请手动设置为false
 */
export function openAppNaPage(params = {}, opts = {}) {
    let scheme = '';
    let universalLink = '';
    const {fail, wait, useCopy, newCN, invokeCN, useULink} = opts;

    if (isPlain(params)) {
        const p = pickup(params, ['id', 'type', 'version', 'source', 'ext']);
        const pValue = encodeURIComponent(JSON.stringify(p));
        scheme = `${SCHEME_PREFIX_OF_OPEN_APP_NA_PAGE}${pValue}`;
        universalLink = `${UNIVERSAL_LINK_PREFIX}${pValue}&func_type=invoke`;
    }
    else {
        scheme = universalLink = params;
    }
    scheme = addUbcExt(scheme, invokeCN, newCN);
    if (IS_BAIDU) {
        scheme += '&zcmapp_from=baiduapp';
        scheme += `&backurl=${encodeURIComponent('baiduboxapp://donothing')}`;
    }
    if (useCopy) {
        copy2clipboard(scheme);
    }
    if (CAN_I_USE_ULINK && useULink !== false) {
        window.location.href = universalLink;
        return;
    }

    openApp({scheme, fail, wait, newCN, invokeCN});
}

/**
 * 打开端内小程序页面
 * 如果对params的参数配置不了解，可以参考文件头部规范文档
 *
 * @param {Object|string} params scheme或者是生成scheme必要的参数
 * @param {string} params.path 小程序页面路径及参数
 * @param {Object} opts 自定义配置
 * @param {Function} opts.fail 失败回调
 * @param {number} opts.wait 失败回调的定时，毫秒
 * @param {boolean} opts.useCopy 是否复制剪切板
 * @param {boolean} opts.newCN 新增渠道号
 * @param {boolean} opts.invokeCN 唤起渠道号
 * @param {boolean} opts.useULink 默认使用ulink，如果想禁用请手动设置为false
 */
export function openAppSwanPage(params = {}, opts = {}) {
    let scheme = '';
    let universalLink = '';
    const {fail, wait, useCopy, newCN, invokeCN, useULink} = opts;

    if (isPlain(params)) {
        const {path} = params;
        scheme = SCHEME_PREFIX_OF_OPEN_APP_SWAN_PAGE + path;
        const pValue = encodeURIComponent(JSON.stringify({'url': scheme}));
        universalLink = `${UNIVERSAL_LINK_PREFIX}${pValue}&func_type=swan`;
    }
    else {
        scheme = universalLink = params;
    }
    scheme = addUbcExt(scheme, invokeCN, newCN);
    if (IS_BAIDU) {
        scheme += '&zcmapp_from=baiduapp';
        scheme += `&backurl=${encodeURIComponent('baiduboxapp://donothing')}`;
    }
    if (useCopy) {
        copy2clipboard(scheme);
    }
    if (CAN_I_USE_ULINK && useULink !== false) {
        window.location.href = universalLink;
        return;
    }

    openApp({scheme, fail, wait, newCN, invokeCN});
}

/**
 * 打开手百小程序页面
*/
export function openBdboxSwanPage(params, opts) {
    let scheme = '';
    const {fail, wait, useCopy, newCN, invokeCN} = opts;

    if (isPlain(params)) {
        const {path} = params;
        scheme = SCHEME_PREFIX_OF_OPEN_BDBOX_SWAN_PAGE + path;
    }
    scheme = addUbcExt(scheme, invokeCN, newCN);
    if (useCopy) {
        copy2clipboard(scheme);
    }
    if (IS_BAIDU) {
        scheme += '&zcmapp_from=baiduapp';
        scheme += `&backurl=${encodeURIComponent('baiduboxapp://donothing')}`;
        openApp({scheme, fail, wait, newCN, invokeCN});
    }
    else {
        const {path} = params;
        window.location.href = `https://mbd.baidu.com/ma/landingpage?t=smartapp_share&scenario=share&appid=s1RSsNQbWmxAuXu05icm1lw0GCyH25NM&url=${encodeURIComponent(path)}`;
    }
}

/**
 * 打开端内H5页面
 * 如果对params的参数配置不了解，可以参考文件头部规范文档
 *
 * @param {Object|string} params scheme或者是生成scheme必要的参数
 * @param {string} params.url 页面url
 * @param {string} params.title 页面标题
 * @param {boolean} params.show_nav_bar 用于控制是否展示顶部导航+标题栏
 * @param {Object} opts 自定义配置
 * @param {Function} opts.fail 失败回调
 * @param {number} opts.wait 失败回调的定时，毫秒
 * @param {boolean} opts.useCopy 是否复制剪切板
 * @param {boolean} opts.newCN 新增渠道号
 * @param {boolean} opts.invokeCN 唤起渠道号
 * @param {boolean} opts.useULink 默认使用ulink，如果想禁用请手动设置为false
 */
export function openAppH5Page(params = {}, opts = {}) {
    let scheme = '';
    let universalLink = '';
    const {fail, wait, useCopy, newCN, invokeCN, useULink} = opts;

    if (isPlain(params)) {
        const p = pickup(params, ['url', 'title', 'show_nav_bar']);
        const pValue = encodeURIComponent(JSON.stringify(p));
        scheme = `${SCHEME_PREFIX_OF_OPEN_APP_H5_PAGE}${pValue}`;
        universalLink = `${UNIVERSAL_LINK_PREFIX}${pValue}&func_type=invokeh5`;
    }
    else {
        scheme = universalLink = params;
    }
    scheme = addUbcExt(scheme, invokeCN, newCN);
    if (IS_BAIDU) {
        scheme += '&zcmapp_from=baiduapp';
        scheme += `&backurl=${encodeURIComponent('baiduboxapp://donothing')}`;
    }
    if (useCopy) {
        copy2clipboard(scheme);
    }
    if (CAN_I_USE_ULINK && useULink !== false) {
        window.location.href = universalLink;
        return;
    }
    openApp({scheme, fail, wait, newCN, invokeCN});
}

/**
 * 打开端内直播预告页
 * 如果对params的参数配置不了解，可以参考文件头部规范文档
 *
 * @param {Object|string} params scheme或者是生成scheme必要的参数
 * @param {string} params.gem_id
 * @param {number} params.gem_type
 * @param {string} params.live_id
 * @param {number} params.entry
 * @param {boolean} params.live_cover
 * @param {Object} opts 自定义配置
 * @param {Function} opts.fail 失败回调
 * @param {number} opts.wait 失败回调的定时，毫秒
 * @param {boolean} opts.useCopy 是否复制剪切板
 * @param {boolean} opts.newCN 新增渠道号
 * @param {boolean} opts.invokeCN 唤起渠道号
 * @param {boolean} opts.useULink 默认使用ulink，如果想禁用请手动设置为false
 */
export function openAppLiveTrailerPage(params = {}, opts = {}) {
    let scheme = '';
    let universalLink = '';
    const {fail, wait, useCopy, newCN, invokeCN, useULink} = opts;

    if (isPlain(params)) {
        const p = pickup(params, ['gem_id', 'gem_type', 'live_id', 'entry', 'live_cover']);
        const pValue = encodeURIComponent(JSON.stringify(p));
        scheme = `${SCHEME_PREFIX_OF_OPEN_APP_LIVE_BANNER}${pValue}`;
        universalLink = `${UNIVERSAL_LINK_PREFIX}${pValue}&func_type=livebanner`;
    }
    else {
        scheme = universalLink = params;
    }
    scheme = addUbcExt(scheme, invokeCN, newCN);
    if (IS_BAIDU) {
        scheme += '&zcmapp_from=baiduapp';
        scheme += `&backurl=${encodeURIComponent('baiduboxapp://donothing')}`;
    }
    if (useCopy) {
        copy2clipboard(scheme);
    }
    if (CAN_I_USE_ULINK && useULink !== false) {
        window.location.href = universalLink;
        return;
    }

    openApp({scheme, fail, wait, newCN, invokeCN});
}

/**
 * 打开端内直播间页
 * 如果对params的参数配置不了解，可以参考文件头部规范文档
 *
 * @param {Object|string} params scheme或者是生成scheme必要的参数
 * @param {string} params.live_id
 * @param {number} params.live_type
 * @param {Object} opts 自定义配置
 * @param {Function} opts.fail 失败回调
 * @param {number} opts.wait 失败回调的定时，毫秒
 * @param {boolean} opts.useCopy 是否复制剪切板
 * @param {boolean} opts.newCN 新增渠道号
 * @param {boolean} opts.invokeCN 唤起渠道号
 * @param {boolean} opts.useULink 默认使用ulink，如果想禁用请手动设置为false
 */
export function openAppLiveRoomPage(params = {}, opts = {}) {
    let scheme = '';
    let universalLink = '';
    const {fail, wait, useCopy, newCN, invokeCN, useULink} = opts;

    if (isPlain(params)) {
        const p = pickup(params, ['live_id', 'live_type']);
        const pValue = encodeURIComponent(JSON.stringify(p));
        scheme = `${SCHEME_PREFIX_OF_OPEN_APP_LIVE_ROOM}${pValue}`;
        universalLink = `${UNIVERSAL_LINK_PREFIX}${pValue}&func_type=liveroom`;

    }
    else {
        scheme = universalLink = params;
    }
    scheme = addUbcExt(scheme, invokeCN, newCN);
    if (IS_BAIDU) {
        scheme += '&zcmapp_from=baiduapp';
        scheme += `&backurl=${encodeURIComponent('baiduboxapp://donothing')}`;
    }
    if (useCopy) {
        copy2clipboard(scheme);
    }
    if (CAN_I_USE_ULINK && useULink !== false) {
        window.location.href = universalLink;
        return;
    }

    openApp({scheme, fail, wait, newCN, invokeCN});
}

/**
 * 打开端内直播回放页
 * 如果对params的参数配置不了解，可以参考文件头部规范文档
 *
 * @param {Object|string} params scheme或者是生成scheme必要的参数
 * @param {string} params.gem_id
 * @param {number} params.gem_type
 * @param {string} params.live_id
 * @param {number} params.entry
 * @param {Object} opts 自定义配置
 * @param {Function} opts.fail 失败回调
 * @param {number} opts.wait 失败回调的定时，毫秒
 * @param {boolean} opts.useCopy 是否复制剪切板
 * @param {boolean} opts.newCN 新增渠道号
 * @param {boolean} opts.invokeCN 唤起渠道号
 * @param {boolean} opts.useULink 默认使用ulink，如果想禁用请手动设置为false
 */
export function openAppLivePlaybackPage(params = {}, opts = {}) {
    let scheme = '';
    let universalLink = '';
    const {fail, wait, useCopy, newCN, invokeCN, useULink} = opts;

    if (isPlain(params)) {
        const p = pickup(params, ['gem_id', 'gem_type', 'live_id', 'entry']);
        const pValue = encodeURIComponent(JSON.stringify(p));
        scheme = `${SCHEME_PREFIX_OF_OPEN_APP_LIVE_PLAYBACK}${pValue}`;
        universalLink = `${UNIVERSAL_LINK_PREFIX}${pValue}&func_type=liveplayback`;
    }
    else {
        scheme = universalLink = params;
    }
    scheme = addUbcExt(scheme, invokeCN, newCN);
    if (IS_BAIDU) {
        scheme += '&zcmapp_from=baiduapp';
        scheme += `&backurl=${encodeURIComponent('baiduboxapp://donothing')}`;
    }
    if (useCopy) {
        copy2clipboard(scheme);
    }
    if (CAN_I_USE_ULINK && useULink !== false) {
        window.location.href = universalLink;
        return;
    }

    openApp({scheme, fail, wait, newCN, invokeCN});
}

/**
 * 打开app
 *
 * @param {Object} opts 参数对象
 * @param {string} opts.scheme scheme字符串
 * @param {Function} opts.fail 打开失败回调
 * @param {number} opts.wait 等待时长，超时后执行回调
 * @param {string} opts.newCN 新增渠道号
 * @param {string} opts.invokeCN 唤起渠道号
 */
export default function openApp({scheme, fail, wait = WAIT_TIME, newCN, invokeCN}) {
    invokeApp({
        scheme,
        failCb: scheme => {
            // 若自定义fail，从返回值判断是否执行默认下载逻辑
            if (fail && !fail()) {
                return;
            }
            openFailCbk(scheme, newCN, invokeCN);
        },
        waitTime: wait
    });
}

/**
 * 将 ubc 配置信息加到 scheme 中
 *
 * @param {string} scheme 旧的 scheme
 * @param {string} channelNumber 渠道号
 * @param {string} downloadNumber 新增渠道号
 * @return {string} 新的 scheme
 */
export function addUbcExt(scheme, invokeCN, newCN) {
    return `${scheme}&ubc_ext=`
            + encodeURIComponent(JSON.stringify({'invoke_channel': invokeCN, 'download_channel': newCN}));
}

/**
 * 手百内通过端能力打开app（待白名单后修改）
 *
 * @param {Object} opts 参数对象
 * @param {string} opts.scheme scheme字符串
 * @param {Function} opts.fail 打开失败回调
 * @param {string} opts.newCN 新增渠道号
 * @param {string} opts.invokeCN 唤起渠道号
 */
export function openAppInBdbox({scheme, fail, newCN, invokeCN}) {
    launch3rdApp({
        // 对应客户端id, 用来打点统计
        appId: '1523487452',
        // 对应客户端调起scheme
        scheme: scheme,
        // 区分活动渠道, 用来统计调起量
        from: invokeCN,
        // 协议状态回调，非必传
        callback: () => {}
    })// 判断是否调起的回调
        .then(result => {
            if (+result.status !== 1) {
            // 调起失败，进入下载流程
                fail(scheme, newCN, invokeCN);
            }
        });
}

/**
 * 手百内通过端能力下载安卓apk
 *
 * @param {string} url apk链接
 */
export function downloadInBdbox(url) {
    // 多个apk下载能力
    let stemp = guid();
    let callbacnName = 'callbacks_' + stemp;
    window[callbacnName] = {};
    window[callbacnName].dlCallback = res => {
        try {
            window.APKdownloadInfo = JSON.parse(res).data;
        }
        catch {
            window.APKdownloadInfo = {msg: 'error'};
        }
        registerDownloadChannel(window.getGemcatDownloadProcess);
    };

    let dlScheme = 'baiduboxapp://v14/download/app?params='
        + encodeURIComponent(JSON.stringify({
            'type': 'startdownload',
            'business': 'zhaocaimao',
            'url': url,
            'file_id': 'gemcatapk' + stemp,
            'callback': 'window["' + callbacnName + '"].dlCallback'
        }));
    window.location.href = dlScheme;
}
